import {Directive, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, Renderer2} from "@angular/core";

// box geometry in 1020x1320 pixel space.
export interface ZonalBox {
    x: number;
    y: number;
    width: number;
    height: number;
}

// native (no jQuery UI) drag + resize for a zonal region overlay.
// drag anywhere on the box body to move it; drag the bottom-right grip to resize.
// emits the new geometry on every move and a final value when the gesture ends.
@Directive({
    selector: "[appZonalRegion]",
    standalone: false
})
export class ZonalRegionDirective implements OnChanges {
    @Input("appZonalRegion") box: ZonalBox = {x: 0, y: 0, width: 0, height: 0};

    // minimum box size in pixel space.
    @Input() minSize = 8;

    @Output() boxChange = new EventEmitter<ZonalBox>();
    @Output() boxChangeEnd = new EventEmitter<ZonalBox>();

    private mode: "none" | "move" | "resize" = "none";
    private startPointerX = 0;
    private startPointerY = 0;
    private startBox: ZonalBox = {x: 0, y: 0, width: 0, height: 0};
    private moveUnlisten: (() => void) | null = null;
    private upUnlisten: (() => void) | null = null;

    constructor(
        private readonly el: ElementRef<HTMLElement>,
        private readonly renderer: Renderer2
    ) {}

    ngOnChanges(): void {
        this.applyStyle();
    }

    // start a gesture. a target carrying the resize-grip marker resizes, otherwise we move.
    @HostListener("pointerdown", ["$event"])
    onPointerDown(event: PointerEvent): void {
        const target = event.target as HTMLElement;
        // ignore clicks on the region action buttons.
        if (target.closest(".zonal-region-btn")) {
            return;
        }
        this.mode = target.closest(".zonal-resize-grip") ? "resize" : "move";
        this.startPointerX = event.clientX;
        this.startPointerY = event.clientY;
        this.startBox = {...this.box};

        event.preventDefault();
        event.stopPropagation();

        this.moveUnlisten = this.renderer.listen("document", "pointermove", e => this.onPointerMove(e));
        this.upUnlisten = this.renderer.listen("document", "pointerup", e => this.onPointerUp(e));
    }

    private onPointerMove(event: PointerEvent): void {
        if (this.mode === "none") {
            return;
        }
        const dx = event.clientX - this.startPointerX;
        const dy = event.clientY - this.startPointerY;

        if (this.mode === "move") {
            this.box = {
                ...this.box,
                x: this.startBox.x + dx,
                y: this.startBox.y + dy
            };
        } else {
            this.box = {
                ...this.box,
                width: Math.max(this.minSize, this.startBox.width + dx),
                height: Math.max(this.minSize, this.startBox.height + dy)
            };
        }

        this.applyStyle();
        this.boxChange.emit(this.box);
    }

    private onPointerUp(_event: PointerEvent): void {
        if (this.mode === "none") {
            return;
        }
        this.mode = "none";
        this.detach();
        this.boxChangeEnd.emit(this.box);
    }

    private detach(): void {
        if (this.moveUnlisten) {
            this.moveUnlisten();
            this.moveUnlisten = null;
        }
        if (this.upUnlisten) {
            this.upUnlisten();
            this.upUnlisten = null;
        }
    }

    // push the model geometry onto the element so the DOM matches the in-memory box.
    private applyStyle(): void {
        const host = this.el.nativeElement;
        this.renderer.setStyle(host, "left", `${this.box.x}px`);
        this.renderer.setStyle(host, "top", `${this.box.y}px`);
        this.renderer.setStyle(host, "width", `${this.box.width}px`);
        this.renderer.setStyle(host, "height", `${this.box.height}px`);
    }
}
